import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, iif, takeUntil } from 'rxjs';
import {
	FinalReportResponse,
	MasarFinalReportResponse,
} from '../../../models/final-report-response.model';
import { BaseComponent } from '../../../base.component';
import { CriterionsService } from '../../../services/criterions.service';
import { showToast } from '../../../../toast.helper';
import { MasarCriterionsService } from '../../../services/masar-criterions.service';
import { ExecutivePlan } from '../../../models/executive-plan.model';
import { ExecuteVisitType } from '../../../models/execute-visit.model';
import { Summary } from '../../../models/summary.model';

@Component({
	selector: 'app-masar-add-edit-response',
	templateUrl: './masar-add-edit-response.dialog.html',
	styleUrls: ['./masar-add-edit-response.dialog.scss'],
})
export class MasarAddEditResponseComponent extends BaseComponent {
	responses = ['accept', 'accept_update', 'reject'];
	leaderDecisions = ['accept', 'reject'];
	form: FormGroup;
	leaderDecisionform: FormGroup;
	isLoadingResults = false;
	modelId: number;
	recommendationId: number;
	executivePlan: ExecutivePlan[] = [];

	get reasonsControl() {
		return this.form.get('reasons');
	}
	get updatesControl() {
		return this.form.get('updates') as FormArray;
	}
	get updatesControls() {
		return (this.form.get('updates') as FormArray).controls as FormControl[];
	}
	orginalResponseDecisionValue: string;
	get responseDecisionValue() {
		return this.form.controls.responseDecision.value;
	}

	get isLeaderDecision() {
		const executeVisitType = this.criterionsService.decisionInputs$.getValue().executeVisitType;
		return (
			executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries ||
			!!this.data.summary.leaderDecision
		);
	}

	get disableLeaderDecision() {
		const executeVisitType = this.criterionsService.decisionInputs$.getValue().executeVisitType;
		return executeVisitType != ExecuteVisitType.FinalReportWithInstitutionEntries;
	}
	get leaderDecisionValue() {
		return this.leaderDecisionform.controls.leaderDecision.value;
	}
	get leaderRejectReason() {
		return this.leaderDecisionform.get('leaderRejectReason');
	}

	constructor(
		public dialogRef: MatDialogRef<MasarAddEditResponseComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private fb: FormBuilder,
		private criterionsService: CriterionsService,
		private masarCriterionsService: MasarCriterionsService,
		private toastrService: ToastrService,
		private translateService: TranslateService
	) {
		super();

		this.recommendationId = this.data.summaryId;
		if (this.data.summary?.rejected) {
			this.data.viewOnly = true;
		}

		this.initForm();
		this.registerUpdateFormValidation();
		this.getData();
	}

	addUpdate(value: string = '') {
		this.updatesControl.push(
			new FormControl({ value: value, disabled: this.data.viewOnly }, Validators.required)
		);
	}
	removeUpdate(index: number) {
		this.updatesControl.removeAt(index);
	}

	changeExecutivePlan(data: ExecutivePlan[]) {
		this.executivePlan = data;
	}

	save() {
		if (this.form.invalid) return;
		this.isLoadingResults = true;

		const model: MasarFinalReportResponse = {
			response: {
				id: this.modelId,
				visitKey: this.data.visitMetaData.visitKey,
				recommendationsSummaryId: this.data.summaryId,
				...this.form.value,
			},
			plans: this.executivePlan,
		};

		this.masarCriterionsService
			.createResponsesWithExecutive(model)
			.pipe(finalize(() => (this.isLoadingResults = false)))
			.subscribe((data) => {
				this.dialogRef.close(data.id);
				showToast(
					this.toastrService,
					this.translateService.instant('forms.common.Criteria.Saved done'),
					this.translateService.instant('forms.common.Criteria.Saved Successfully'),
					false
				);
			});
	}
	saveLeaderDecision() {
		if (this.leaderDecisionform.invalid) return;
		this.isLoadingResults = true;

		const formModel = this.leaderDecisionform.value;
		const model: Summary = {
			...this.data.summary,
			...formModel,
			rejected: formModel.leaderDecision == 'accept' && this.responseDecisionValue == 'reject',
		};

		this.masarCriterionsService
			.saveRecommendationsSummaries(model)
			.pipe(finalize(() => (this.isLoadingResults = false)))
			.subscribe((data) => {
				this.dialogRef.close(this.modelId);
				showToast(
					this.toastrService,
					this.translateService.instant('forms.common.Criteria.Saved done'),
					this.translateService.instant('forms.common.Criteria.Saved Successfully'),
					false
				);
			});
	}
	cancel(): void {
		this.dialogRef.close();
	}

	private initForm() {
		this.form = this.fb.group({
			responseDecision: [{ value: '', disabled: this.data.viewOnly }, [Validators.required]],
			reasons: [{ value: '', disabled: this.data.viewOnly }],
			updates: this.fb.array([]),
		});

		this.leaderDecisionform = this.fb.group({
			summaryText: [
				{ value: this.data.summary.summaryText, disabled: this.disableLeaderDecision },
				[Validators.required],
			],
			leaderDecision: [{ value: '', disabled: this.disableLeaderDecision }, [Validators.required]],
			leaderRejectReason: [{ value: '', disabled: this.disableLeaderDecision }],
		});
	}
	private registerUpdateFormValidation() {
		this.form.controls.responseDecision.valueChanges.subscribe((value) => {
			if (value != 'accept') {
				this.reasonsControl.setValidators([Validators.required]);
			} else {
				this.reasonsControl.reset();
				this.reasonsControl.clearValidators();
			}
			this.reasonsControl.updateValueAndValidity();

			if (value == 'accept_update') {
				if (!this.updatesControl.length) this.addUpdate();
				this.updatesControl.setValidators([Validators.required]);
			} else {
				this.updatesControl.clear();
				this.updatesControl.clearValidators();
			}
			this.updatesControl.updateValueAndValidity();
		});

		this.leaderDecisionform.controls.leaderDecision.valueChanges.subscribe((value) => {
			if (value != 'accept') {
				this.leaderRejectReason.setValidators([Validators.required]);
			} else {
				this.leaderRejectReason.reset();
				this.leaderRejectReason.clearValidators();
			}
			this.leaderRejectReason.updateValueAndValidity();
		});
	}

	private getData() {
		if (!this.data.id) return;
		this.isLoadingResults = true;
		this.masarCriterionsService
			.getFinalReportResponses(this.data.id)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => (this.isLoadingResults = false))
			)
			.subscribe((data) => {
				this.updateform(data);
			});
	}
	private updateform(data: FinalReportResponse) {
		this.modelId = data.id;
		this.recommendationId = data.recommendationsSummaryId;
		this.orginalResponseDecisionValue = data.responseDecision;
		this.form.patchValue({
			responseDecision: data.responseDecision,
			reasons: data.reasons,
		});
		this.updatesControl.clear();
		data.updates.forEach((value, index) => {
			this.addUpdate(value);
		});

		this.leaderDecisionform.patchValue({
			leaderDecision: this.data.summary.leaderDecision,
			leaderRejectReason: this.data.summary.leaderRejectReason,
			summaryText: this.data.summary.summaryText,
		});
	}
}
